import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import DeptAdminDashboard from "./pages/DeptAdminDashboard";
import UserDashboard from "./pages/UserDashboard";
import RuleBuilderPage from "./pages/RuleBuilderPage";
import ReconciliationWizard from "./pages/ReconciliationWizard";
import ResultsPage from "./pages/ResultsPage";
import ProfilePage from "./pages/ProfilePage";
import LookupTablesPage from "./pages/LookupTablesPage";
import CleanupTablesPage from "./pages/CleanupTablesPage";
import RegexPatternsPage from "./pages/RegexPatternsPage";
import MultiReconciliationWizard from "./pages/MultiReconciliationWizard";
import MultiResultsPage from "./pages/MultiResultsPage";
import ChainReconciliationWizard from "./pages/ChainReconciliationWizard";
import ChainResultsPage from "./pages/ChainResultsPage";
import ChainTemplatesPage from "./pages/ChainTemplatesPage";
import SchedulesPage from "./pages/SchedulesPage";
import NotificationPrefsPage from "./pages/NotificationPrefsPage";
import KnowledgeBasePage from "./pages/KnowledgeBasePage";
import WebhooksPage from "./pages/WebhooksPage";
import TransformSetsPage from "./pages/TransformSetsPage";
import TransformSetBuilderPage from "./pages/TransformSetBuilderPage";
import FileTemplatesPage from "./pages/FileTemplatesPage";
import ReportBuilderPage from "./pages/ReportBuilderPage";
import SettingsPage from "./pages/SettingsPage";
import SuperAdminsPage from "./pages/SuperAdminsPage";
import PricingPlansPage from "./pages/PricingPlansPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";

function HomeRouter() {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Logged-in users go straight to their dashboard; everyone else sees the landing page
  if (user) {
    if (user.role === "superadmin") return <Navigate to="/admin" replace />;
    if (user.role === "dept_admin") return <Navigate to="/dept-admin" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <LandingPage />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<HomeRouter />} />
              <Route path="/billing/success" element={<CheckoutSuccessPage />} />
              <Route path="/admin" element={
                <ProtectedRoute roles={["superadmin"]}>
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/settings" element={
                <ProtectedRoute roles={["superadmin"]}>
                  <SettingsPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/plans" element={
                <ProtectedRoute roles={["superadmin"]}>
                  <PricingPlansPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/superadmins" element={
                <ProtectedRoute roles={["superadmin"]}>
                  <SuperAdminsPage />
                </ProtectedRoute>
              } />
            <Route path="/dept-admin" element={
              <ProtectedRoute roles={["dept_admin"]}>
                <DeptAdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute roles={["dept_user"]}>
                <UserDashboard />
              </ProtectedRoute>
            } />
            <Route path="/rule-builder/:id?" element={
              <ProtectedRoute roles={["superadmin", "dept_admin"]}>
                <RuleBuilderPage />
              </ProtectedRoute>
            } />
            <Route path="/reconcile" element={
              <ProtectedRoute roles={["dept_user", "dept_admin"]}>
                <ReconciliationWizard />
              </ProtectedRoute>
            } />
            <Route path="/multi-reconcile" element={
              <ProtectedRoute roles={["dept_user", "dept_admin"]}>
                <MultiReconciliationWizard />
              </ProtectedRoute>
            } />
            <Route path="/chain-reconcile" element={
              <ProtectedRoute roles={["dept_user", "dept_admin"]}>
                <ChainReconciliationWizard />
              </ProtectedRoute>
            } />
            <Route path="/chain-results/:id" element={
              <ProtectedRoute>
                <ChainResultsPage />
              </ProtectedRoute>
            } />
            <Route path="/chain-templates" element={
              <ProtectedRoute>
                <ChainTemplatesPage />
              </ProtectedRoute>
            } />
            <Route path="/schedules" element={
              <ProtectedRoute roles={["superadmin", "dept_admin"]}>
                <SchedulesPage />
              </ProtectedRoute>
            } />
            <Route path="/notifications" element={
              <ProtectedRoute>
                <NotificationPrefsPage />
              </ProtectedRoute>
            } />
            <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
            <Route path="/webhooks" element={
              <ProtectedRoute roles={["superadmin", "dept_admin"]}>
                <WebhooksPage />
              </ProtectedRoute>
            } />
            <Route path="/multi-results/:id" element={
              <ProtectedRoute>
                <MultiResultsPage />
              </ProtectedRoute>
            } />
            <Route path="/results/:runId" element={
              <ProtectedRoute>
                <ResultsPage />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/lookups" element={
              <ProtectedRoute>
                <LookupTablesPage />
              </ProtectedRoute>
            } />
            <Route path="/cleanups" element={
              <ProtectedRoute>
                <CleanupTablesPage />
              </ProtectedRoute>
            } />
            <Route path="/regex" element={
              <ProtectedRoute>
                <RegexPatternsPage />
              </ProtectedRoute>
            } />
            <Route path="/transform-sets" element={
              <ProtectedRoute>
                <TransformSetsPage />
              </ProtectedRoute>
            } />
            <Route path="/transform-sets/:id" element={
              <ProtectedRoute>
                <TransformSetBuilderPage />
              </ProtectedRoute>
            } />
            <Route path="/file-templates" element={
              <ProtectedRoute>
                <FileTemplatesPage />
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute>
                <ReportBuilderPage />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
